import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Divider, CircularProgress, Alert, Chip, IconButton,
  Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Tooltip, Snackbar, Switch, FormControlLabel,
} from '@mui/material';
import {
  ArrowBack, Save, Add, Delete, VideoLibrary, TextFields, DashboardCustomize,
  Quiz, EmojiEvents, Info, Publish,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { contentAPI } from '../services/api';
import { FullCourse, CourseModule, CourseTest, ContentType, TestType } from '../types';
import TextContentEditor from '../components/content/TextContentEditor';
import VideoContentEditor from '../components/content/VideoContentEditor';
import MultimediaContentEditor from '../components/content/MultimediaContentEditor';
import TestBuilder from '../components/tests/TestBuilder';

const DRAWER_WIDTH = 280;

type Selection =
  | { kind: 'info' }
  | { kind: 'module'; id: string }
  | { kind: 'test'; testType: TestType };

const contentTypeIcon = (type: ContentType) => {
  if (type === 'video') return <VideoLibrary fontSize="small" color="error" />;
  if (type === 'multimedia') return <DashboardCustomize fontSize="small" color="secondary" />;
  return <TextFields fontSize="small" color="primary" />;
};

const CourseEditorPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();

  const [course, setCourse] = useState<FullCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Selection>({ kind: 'info' });
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState('');

  // Course info state
  const [info, setInfo] = useState({
    title: '', description: '', category: '', difficulty: 'beginner',
    instructor: '', thumbnail: '', tags: '',
  });

  // Module editor state
  const [modDraft, setModDraft] = useState<CourseModule | null>(null);
  const [modSaving, setModSaving] = useState(false);

  useEffect(() => {
    if (courseId) loadCourse(courseId);
  }, [courseId]);

  const loadCourse = async (id: string) => {
    try {
      const res = await contentAPI.getCourse(id);
      const c = res.data.course;
      setCourse(c);
      setInfo({
        title: c.title,
        description: c.description,
        category: c.category,
        difficulty: c.difficulty,
        instructor: c.instructor,
        thumbnail: c.thumbnail || '',
        tags: (c.tags || []).join(', '),
      });
    } catch {
      setError('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  // When a module is selected, load its draft state
  useEffect(() => {
    if (selected.kind === 'module' && course) {
      const mod = course.modules.find(m => m.id === selected.id);
      setModDraft(mod ? { ...mod } : null);
    }
  }, [selected, course]);

  const saveCourseInfo = async () => {
    if (!course) return;
    setSaving(true);
    try {
      const res = await contentAPI.updateCourse(course.id, {
        title: info.title,
        description: info.description,
        category: info.category,
        difficulty: info.difficulty as any,
        instructor: info.instructor,
        thumbnail: info.thumbnail,
        tags: info.tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      // Merge response with existing state to preserve modules/tests
      setCourse(prev => prev ? { ...prev, ...res.data.course } : res.data.course);
      setSnack('Course info saved');
    } catch {
      setError('Failed to save course info');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    if (!course) return;
    try {
      const res = await contentAPI.publishCourse(course.id);
      setCourse(prev => prev ? { ...prev, ...res.data.course } : res.data.course);
      setSnack(res.data.course.is_active ? 'Course published!' : 'Course unpublished');
    } catch {
      setError('Failed to toggle publish');
    }
  };

  const addModule = async () => {
    if (!course) return;
    try {
      const res = await contentAPI.createModule(course.id, {
        title: 'New Module',
        description: '',
        duration: 30,
        content_type: 'text',
      });
      const newMod = res.data.module;
      setCourse(prev => prev ? { ...prev, modules: [...prev.modules, newMod] } : prev);
      setSelected({ kind: 'module', id: newMod.id });
    } catch {
      setError('Failed to add module');
    }
  };

  const deleteModule = async (modId: string) => {
    if (!course || !window.confirm('Delete this module?')) return;
    try {
      await contentAPI.deleteModule(course.id, modId);
      setCourse(prev => prev ? { ...prev, modules: prev.modules.filter(m => m.id !== modId) } : prev);
      setSelected({ kind: 'info' });
      setSnack('Module deleted');
    } catch {
      setError('Failed to delete module');
    }
  };

  const saveModule = async () => {
    if (!course || !modDraft) return;
    setModSaving(true);
    try {
      const res = await contentAPI.updateModule(course.id, modDraft.id, modDraft as any);
      const updated = res.data.module;
      setCourse(prev => prev ? { ...prev, modules: prev.modules.map(m => m.id === updated.id ? updated : m) } : prev);
      setModDraft(updated);
      setSnack('Module saved');
    } catch {
      setError('Failed to save module');
    } finally {
      setModSaving(false);
    }
  };

  const addTest = async (type: TestType) => {
    if (!course) return;
    const isFinal = type === 'final';
    try {
      const res = await contentAPI.createTest(course.id, {
        title: isFinal ? 'Final Certification Exam' : 'Practice Quiz',
        type,
        time_limit: isFinal ? 30 : 15,
        passing_score: isFinal ? 80 : 60,
        description: isFinal
          ? 'Complete this exam to earn your certificate. You must score at least 80% to pass.'
          : 'Test your knowledge before the final exam. You can retake this as many times as needed.',
        max_attempts: isFinal ? 1 : -1,
      });
      setCourse(prev => {
        if (!prev) return prev;
        return type === 'mock'
          ? { ...prev, mock_test: res.data.test }
          : { ...prev, final_test: res.data.test };
      });
      setSelected({ kind: 'test', testType: type });
      setSnack(`${isFinal ? 'Final exam' : 'Practice quiz'} created`);
    } catch {
      setError('Failed to create test');
    }
  };

  const deleteTest = async (type: TestType) => {
    if (!course || !window.confirm(`Delete the ${type} test?`)) return;
    try {
      await contentAPI.deleteTest(course.id, type);
      setCourse(prev => {
        if (!prev) return prev;
        return type === 'mock' ? { ...prev, mock_test: null } : { ...prev, final_test: null };
      });
      setSelected({ kind: 'info' });
      setSnack('Test deleted');
    } catch {
      setError('Failed to delete test');
    }
  };

  const onTestUpdate = (type: TestType) => (test: CourseTest) => {
    setCourse(prev => {
      if (!prev) return prev;
      return type === 'mock' ? { ...prev, mock_test: test } : { ...prev, final_test: test };
    });
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" pt={8}><CircularProgress /></Box>;
  }

  if (!course) {
    return (
      <Box textAlign="center" pt={8}>
        <Alert severity="error">Course not found</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/content')}>Back to Dashboard</Button>
      </Box>
    );
  }

  const currentTest: CourseTest | null =
    selected.kind === 'test'
      ? (selected.testType === 'mock' ? course.mock_test : course.final_test)
      : null;

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* ── Left Sidebar ── */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            top: 64,
            height: 'calc(100% - 64px)',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {/* Back + Publish */}
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate('/content')} sx={{ mb: 1 }}>
            Dashboard
          </Button>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
              {course.title}
            </Typography>
            <Chip
              label={course.is_active ? 'Live' : 'Draft'}
              color={course.is_active ? 'success' : 'default'}
              size="small"
              onClick={togglePublish}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>

        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <List dense disablePadding>
            {/* Course Info */}
            <ListItemButton
              selected={selected.kind === 'info'}
              onClick={() => setSelected({ kind: 'info' })}
            >
              <ListItemIcon sx={{ minWidth: 36 }}><Info fontSize="small" /></ListItemIcon>
              <ListItemText primary="Course Info" />
            </ListItemButton>

            <Divider />

            {/* Modules */}
            <Box sx={{ px: 1.5, pt: 1, pb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                Modules ({course.modules.length})
              </Typography>
              <Tooltip title="Add Module">
                <IconButton size="small" onClick={addModule}><Add fontSize="small" /></IconButton>
              </Tooltip>
            </Box>

            {course.modules.map((mod, i) => (
              <ListItemButton
                key={mod.id}
                selected={selected.kind === 'module' && selected.id === mod.id}
                onClick={() => setSelected({ kind: 'module', id: mod.id })}
                sx={{ pl: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>{contentTypeIcon(mod.content_type)}</ListItemIcon>
                <ListItemText
                  primary={mod.title || 'Untitled'}
                  secondary={`${mod.duration} min`}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={e => { e.stopPropagation(); deleteModule(mod.id); }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </ListItemButton>
            ))}

            {course.modules.length === 0 && (
              <Box px={2} py={1}>
                <Typography variant="caption" color="text.disabled">No modules yet</Typography>
              </Box>
            )}

            <Divider sx={{ mt: 1 }} />

            {/* Tests */}
            <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                Assessments
              </Typography>
            </Box>

            {/* Mock Test */}
            {course.mock_test ? (
              <ListItemButton
                selected={selected.kind === 'test' && selected.testType === 'mock'}
                onClick={() => setSelected({ kind: 'test', testType: 'mock' })}
                sx={{ pl: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}><Quiz fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText
                  primary="Practice Quiz"
                  secondary={`${course.mock_test.questions.length} questions`}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={e => { e.stopPropagation(); deleteTest('mock'); }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </ListItemButton>
            ) : (
              <ListItemButton onClick={() => addTest('mock')} sx={{ pl: 1 }}>
                <ListItemIcon sx={{ minWidth: 32 }}><Add fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText
                  primary="Add Practice Quiz"
                  primaryTypographyProps={{ variant: 'body2', color: 'warning.main' }}
                />
              </ListItemButton>
            )}

            {/* Final Test */}
            {course.final_test ? (
              <ListItemButton
                selected={selected.kind === 'test' && selected.testType === 'final'}
                onClick={() => setSelected({ kind: 'test', testType: 'final' })}
                sx={{ pl: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}><EmojiEvents fontSize="small" color="success" /></ListItemIcon>
                <ListItemText
                  primary="Final Exam"
                  secondary={`${course.final_test.questions.length} questions`}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={e => { e.stopPropagation(); deleteTest('final'); }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </ListItemButton>
            ) : (
              <ListItemButton onClick={() => addTest('final')} sx={{ pl: 1 }}>
                <ListItemIcon sx={{ minWidth: 32 }}><Add fontSize="small" color="success" /></ListItemIcon>
                <ListItemText
                  primary="Add Final Exam"
                  primaryTypographyProps={{ variant: 'body2', color: 'success.main' }}
                />
              </ListItemButton>
            )}
          </List>
        </Box>

        {/* Publish Button */}
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            fullWidth
            variant={course.is_active ? 'outlined' : 'contained'}
            color={course.is_active ? 'warning' : 'success'}
            startIcon={<Publish />}
            onClick={togglePublish}
          >
            {course.is_active ? 'Unpublish' : 'Publish Course'}
          </Button>
        </Box>
      </Drawer>

      {/* ── Main Editor Area ── */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3, ml: `${DRAWER_WIDTH}px` }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* ── Course Info Panel ── */}
        {selected.kind === 'info' && (
          <Box maxWidth={800}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5" fontWeight={700}>Course Information</Typography>
              <Button variant="contained" startIcon={<Save />} onClick={saveCourseInfo} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>

            <Box display="flex" flexDirection="column" gap={2.5}>
              <TextField
                label="Course Title *"
                fullWidth
                value={info.title}
                onChange={e => setInfo(p => ({ ...p, title: e.target.value }))}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={4}
                value={info.description}
                onChange={e => setInfo(p => ({ ...p, description: e.target.value }))}
                placeholder="Describe what learners will learn, prerequisites, and outcomes..."
              />
              <Box display="flex" gap={2}>
                <TextField
                  label="Category"
                  fullWidth
                  value={info.category}
                  onChange={e => setInfo(p => ({ ...p, category: e.target.value }))}
                  placeholder="e.g. Software Testing"
                />
                <FormControl fullWidth>
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    value={info.difficulty}
                    label="Difficulty"
                    onChange={e => setInfo(p => ({ ...p, difficulty: e.target.value }))}
                  >
                    <MenuItem value="beginner">Beginner</MenuItem>
                    <MenuItem value="intermediate">Intermediate</MenuItem>
                    <MenuItem value="advanced">Advanced</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TextField
                label="Instructor Name"
                fullWidth
                value={info.instructor}
                onChange={e => setInfo(p => ({ ...p, instructor: e.target.value }))}
              />
              <TextField
                label="Thumbnail URL"
                fullWidth
                value={info.thumbnail}
                onChange={e => setInfo(p => ({ ...p, thumbnail: e.target.value }))}
                placeholder="https://..."
                helperText="Leave blank for a gradient placeholder"
              />
              <TextField
                label="Tags (comma-separated)"
                fullWidth
                value={info.tags}
                onChange={e => setInfo(p => ({ ...p, tags: e.target.value }))}
                placeholder="testing, automation, selenium"
              />
            </Box>
          </Box>
        )}

        {/* ── Module Editor Panel ── */}
        {selected.kind === 'module' && modDraft && (
          <Box maxWidth={900}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5" fontWeight={700}>Edit Module</Typography>
              <Button variant="contained" startIcon={<Save />} onClick={saveModule} disabled={modSaving}>
                {modSaving ? 'Saving...' : 'Save Module'}
              </Button>
            </Box>

            <Box display="flex" flexDirection="column" gap={2.5}>
              <Box display="flex" gap={2}>
                <TextField
                  label="Module Title *"
                  fullWidth
                  value={modDraft.title}
                  onChange={e => setModDraft(p => p ? { ...p, title: e.target.value } : p)}
                />
                <TextField
                  label="Duration (min)"
                  type="number"
                  value={modDraft.duration}
                  onChange={e => setModDraft(p => p ? { ...p, duration: parseInt(e.target.value) || 30 } : p)}
                  sx={{ width: 160 }}
                  inputProps={{ min: 1 }}
                />
              </Box>
              <TextField
                label="Module Description"
                fullWidth
                value={modDraft.description}
                onChange={e => setModDraft(p => p ? { ...p, description: e.target.value } : p)}
                placeholder="Briefly describe what this module covers..."
              />

              {/* Content Type Selector */}
              <FormControl fullWidth>
                <InputLabel>Content Type</InputLabel>
                <Select
                  value={modDraft.content_type}
                  label="Content Type"
                  onChange={e => setModDraft(p => p ? { ...p, content_type: e.target.value as ContentType, content: {} } : p)}
                >
                  <MenuItem value="text">
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextFields fontSize="small" color="primary" /> Text / Rich Content
                    </Box>
                  </MenuItem>
                  <MenuItem value="video">
                    <Box display="flex" alignItems="center" gap={1}>
                      <VideoLibrary fontSize="small" color="error" /> Video (YouTube, Vimeo, URL)
                    </Box>
                  </MenuItem>
                  <MenuItem value="multimedia">
                    <Box display="flex" alignItems="center" gap={1}>
                      <DashboardCustomize fontSize="small" color="secondary" /> Multimedia / Interactive
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <Divider />

              {/* Content Editor */}
              {modDraft.content_type === 'text' && (
                <TextContentEditor
                  value={modDraft.content?.html || ''}
                  onChange={html => setModDraft(p => p ? { ...p, content: { html } } : p)}
                />
              )}

              {modDraft.content_type === 'video' && (
                <VideoContentEditor
                  url={modDraft.content?.url || ''}
                  description={modDraft.content?.description || ''}
                  onChange={data => setModDraft(p => p ? { ...p, content: data } : p)}
                />
              )}

              {modDraft.content_type === 'multimedia' && (
                <MultimediaContentEditor
                  embedUrl={modDraft.content?.embed_url || ''}
                  description={modDraft.content?.description || ''}
                  interactiveType={modDraft.content?.interactive_type || 'embed'}
                  onChange={data => setModDraft(p => p ? { ...p, content: data } : p)}
                />
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={modDraft.is_published}
                    onChange={e => setModDraft(p => p ? { ...p, is_published: e.target.checked } : p)}
                  />
                }
                label="Published (visible to learners)"
              />
            </Box>
          </Box>
        )}

        {/* ── Test Builder Panel ── */}
        {selected.kind === 'test' && currentTest && (
          <Box maxWidth={800}>
            <Typography variant="h5" fontWeight={700} mb={3}>
              {selected.testType === 'mock' ? 'Practice Quiz Builder' : 'Final Exam Builder'}
            </Typography>
            <TestBuilder
              key={currentTest.id}
              courseId={course.id}
              testType={selected.testType}
              test={currentTest}
              onTestUpdate={onTestUpdate(selected.testType)}
            />
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack('')}
        message={snack}
      />
    </Box>
  );
};

export default CourseEditorPage;
